import React, { useState, useEffect } from 'react';
import {
  FloatingDrawer,
  FloatingDrawerHeader,
  FloatingDrawerBody,
  FloatingDrawerFooter,
} from '@/components/ui/floating-drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RoleBadge } from './RoleBadge';
import { InviteMemberData } from '@/lib/api/services/teamService';
import { useAuth } from '@/components/AuthProvider';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { Eye, EyeOff, Copy, Check, Building2, Phone, CheckCircle, AlertCircle, UserPlus, Info } from 'lucide-react';
import {
  hasOrgWideBusinessAccountAccess,
  ORG_WIDE_ACCESS_NOTE,
} from '@/lib/teams/businessAccountAccess';

interface MinimalInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (userData: InviteMemberData) => void;
  lockedWorkspaceId?: string | null;
}

export const MinimalInviteDialog: React.FC<MinimalInviteDialogProps> = ({
  open,
  onOpenChange,
  onInvite,
  lockedWorkspaceId = null,
}) => {
  const { user: currentUser } = useAuth();
  const roleRaw = (currentUser?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(currentUser as any)?.is_super_admin;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin;
  const isRegularUser = !isAdmin && roleRaw === 'USER';
  const defaultRole: InviteMemberData['role'] = isRegularUser ? 'AGENT' : 'USER';
  const [formData, setFormData] = useState<InviteMemberData>({
    email: '',
    name: '',
    role: defaultRole,
    password: '',
    hasAllConversationsAccess: false
  });
  const [riderPhone, setRiderPhone] = useState('');
  const [riderCountryCode, setRiderCountryCode] = useState('+91');
  const [riderPhoneError, setRiderPhoneError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedBusinessAccounts, setSelectedBusinessAccounts] = useState<string[]>([]);
  const [accountPermissions, setAccountPermissions] = useState<Record<string, { has_all_conversations_access: boolean }>>({});

  const { data: businessAccounts, isLoading: isLoadingBusinessAccounts } = useWorkspaces();

  useEffect(() => {
    if (open) {
      setFormData({ email: '', name: '', role: defaultRole, password: '', hasAllConversationsAccess: false });
      setRiderPhone('');
      setRiderCountryCode('+91');
      setRiderPhoneError('');
      setShowPassword(false);
      setPasswordError('');
      setIsGenerated(false);
      setCopied(false);
      if (lockedWorkspaceId) {
        setSelectedBusinessAccounts([lockedWorkspaceId]);
      } else if (businessAccounts && businessAccounts.length === 1) {
        setSelectedBusinessAccounts([businessAccounts[0].id]);
      } else {
        setSelectedBusinessAccounts([]);
      }
      setAccountPermissions({});
    }
  }, [open, defaultRole, lockedWorkspaceId]);

  const hasOrgWideAccess = hasOrgWideBusinessAccountAccess({ role: formData.role });

  useEffect(() => {
    if (lockedWorkspaceId) {
      setSelectedBusinessAccounts([lockedWorkspaceId]);
      setAccountPermissions({ [lockedWorkspaceId]: { has_all_conversations_access: formData.role !== 'AGENT' } });
    } else if (hasOrgWideAccess && businessAccounts) {
      const allIds = businessAccounts.map(ba => ba.id);
      setSelectedBusinessAccounts(allIds);
      const perms: Record<string, { has_all_conversations_access: boolean }> = {};
      allIds.forEach(id => {
        perms[id] = { has_all_conversations_access: true };
      });
      setAccountPermissions(perms);
    } else if (open && !hasOrgWideAccess && businessAccounts && selectedBusinessAccounts.length === 0) {
      if (businessAccounts.length > 0) {
        setSelectedBusinessAccounts([businessAccounts[0].id]);
      }
    }
  }, [hasOrgWideAccess, businessAccounts, open, lockedWorkspaceId, formData.role]);

  const visibleWorkspaces = React.useMemo(() => {
    if (!businessAccounts) return businessAccounts;
    if (!lockedWorkspaceId) return businessAccounts;
    return businessAccounts.filter((ba) => ba.id === lockedWorkspaceId);
  }, [businessAccounts, lockedWorkspaceId]);

  const isSingleAccount = !!lockedWorkspaceId || !!(businessAccounts && businessAccounts.length === 1);

  const lockedWorkspaceName = lockedWorkspaceId
    ? (businessAccounts || []).find((ba) => ba.id === lockedWorkspaceId)?.name || 'this workspace'
    : null;

  const handleBusinessAccountToggle = (businessAccountId: string) => {
    if (hasOrgWideAccess) return;
    if (isSingleAccount) return;
    setSelectedBusinessAccounts(prev =>
      prev.includes(businessAccountId)
        ? prev.filter(id => id !== businessAccountId)
        : [...prev, businessAccountId]
    );
  };

  const handleValidationToggle = (businessAccountId: string, checked: boolean) => {
    setAccountPermissions(prev => ({
      ...prev,
      [businessAccountId]: { ...prev[businessAccountId], has_all_conversations_access: checked }
    }));
  };

  const handleSelectAll = () => {
    if (businessAccounts) {
      setSelectedBusinessAccounts(businessAccounts.map(ba => ba.id));
    }
  };

  const handleDeselectAll = () => {
    if (hasOrgWideAccess) return;
    setSelectedBusinessAccounts([]);
  };

  const validatePassword = (password: string): string => {
    if (!password) {
      return ''; // Password is optional
    }
    const hasMinLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);

    if (!hasMinLength || !hasUpper || !hasLower || !hasNumber || !hasSpecial) {
      return 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';
    }
    return '';
  };

  const generateRandomPassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const special = "!@#$%^&*";
    const all = upper + lower + numbers + special;
    const chars = [
      upper.charAt(Math.floor(Math.random() * upper.length)),
      lower.charAt(Math.floor(Math.random() * lower.length)),
      numbers.charAt(Math.floor(Math.random() * numbers.length)),
      special.charAt(Math.floor(Math.random() * special.length)),
    ];
    for (let i = 4; i < 12; i++) {
      chars.push(all.charAt(Math.floor(Math.random() * all.length)));
    }
    const password = chars.sort(() => Math.random() - 0.5).join('');
    setFormData(prev => ({ ...prev, password }));
    setPasswordError('');
    setIsGenerated(true);
    setCopied(false);
  };

  const handlePasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, password: value }));
    const error = validatePassword(value);
    setPasswordError(error);
    setIsGenerated(false);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (formData.password) {
      await navigator.clipboard.writeText(formData.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.role === 'RIDER') {
      const digits = riderPhone.replace(/\D/g, '');
      if (digits.length !== 10) {
        setRiderPhoneError('Please enter a valid 10-digit mobile number.');
        return;
      }
    }

    if (formData.password) {
      const error = validatePassword(formData.password);
      if (error) {
        setPasswordError(error);
        return;
      }
    }

    const assignments = selectedBusinessAccounts.map(accountId => ({
      workspace_id: accountId,
      permissions: {
        has_all_conversations_access: formData.role === 'AGENT'
          ? (accountPermissions[accountId]?.has_all_conversations_access || false)
          : true
      }
    }));

    const inviteData: InviteMemberData = {
      email: formData.email,
      name: formData.name,
      role: formData.role,
      assignments,
      businessAccountIds: selectedBusinessAccounts,
      hasAllConversationsAccess: false,
      ...(formData.role === 'RIDER' && riderPhone.trim() ? { phone: `${riderCountryCode}${riderPhone.replace(/\D/g, '')}` } : {}),
    };

    if (formData.password && typeof formData.password === 'string' && formData.password.trim().length > 0) {
      inviteData.password = formData.password.trim();
    }

    onInvite(inviteData);
  };

  return (
    <FloatingDrawer
      open={open}
      onOpenChange={onOpenChange}
      side="right"
      width={560}
      className="overflow-hidden"
    >
      <FloatingDrawerHeader
        title="Add team member"
        subtitle={lockedWorkspaceName ? `They join ${lockedWorkspaceName} only` : 'Add a new member to your team'}
        icon={<UserPlus className="h-4 w-4" />}
      />

      <FloatingDrawerBody>
        <form id="invite-member-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                modal={false}
                value={formData.role}
                onValueChange={(value) => {
                  if (isRegularUser) {
                    setFormData(prev => ({ ...prev, role: 'AGENT' }));
                    return;
                  }
                  setFormData(prev => ({ ...prev, role: value as 'ADMIN' | 'AGENT' | 'USER' }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {!isRegularUser && (
                    <SelectItem value="USER">
                      <RoleBadge role="USER" size="sm" showIcon={false} />
                    </SelectItem>
                  )}
                  <SelectItem value="AGENT">
                    <RoleBadge role="AGENT" size="sm" showIcon={false} />
                  </SelectItem>
                  <SelectItem value="RIDER">
                    <RoleBadge role="RIDER" size="sm" showIcon={false} />
                  </SelectItem>
                  {isAdmin && (
                    <SelectItem value="ADMIN">
                      <RoleBadge role="ADMIN" size="sm" showIcon={false} />
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'RIDER' && (
              <div className="space-y-2">
                <Label htmlFor="rider-mobile">
                  Mobile Number <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Select value={riderCountryCode} onValueChange={setRiderCountryCode} modal={false}>
                    <SelectTrigger className="w-[110px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      <SelectItem value="+1">🇺🇸 +1</SelectItem>
                      <SelectItem value="+44">🇬🇧 +44</SelectItem>
                      <SelectItem value="+971">🇦🇪 +971</SelectItem>
                      <SelectItem value="+61">🇦🇺 +61</SelectItem>
                      <SelectItem value="+65">🇸🇬 +65</SelectItem>
                      <SelectItem value="+60">🇲🇾 +60</SelectItem>
                      <SelectItem value="+880">🇧🇩 +880</SelectItem>
                      <SelectItem value="+92">🇵🇰 +92</SelectItem>
                      <SelectItem value="+94">🇱🇰 +94</SelectItem>
                      <SelectItem value="+977">🇳🇵 +977</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="rider-mobile"
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter 10-digit number"
                    value={riderPhone}
                    maxLength={10}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setRiderPhone(val);
                      setRiderPhoneError(val.length > 0 && val.length !== 10 ? 'Enter a valid 10-digit number.' : '');
                    }}
                    className={riderPhoneError ? 'border-red-500' : ''}
                    required
                  />
                </div>
                {riderPhoneError ? (
                  <p className="text-xs text-red-500">{riderPhoneError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Rider's mobile number for contact and delivery updates.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateRandomPassword}
                  className="text-xs"
                >
                  Generate Random
                </Button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Leave empty to auto-generate random password"
                  value={formData.password || ''}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={passwordError ? "border-red-500" : ""}
                />
                <div className="absolute right-0 top-0 h-full flex items-center">
                  {isGenerated && formData.password && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="px-2 py-2 hover:bg-transparent"
                      onClick={handleCopyPassword}
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {passwordError && (
                <p className="text-xs text-red-600">{passwordError}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Workspace Access</Label>
              {!isSingleAccount && !hasOrgWideAccess && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Deselect All
                  </Button>
                </div>
              )}
            </div>

            {hasOrgWideAccess ? (
              <div
                className="flex items-start gap-2 rounded-lg border border-[#B2DDFF] bg-[#EFF8FF] px-3 py-2.5 text-xs text-[#175CD3]"
                data-testid="invite-member-org-wide-note"
              >
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>{ORG_WIDE_ACCESS_NOTE}</p>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Choose which workspaces this team member can access and configure permissions.
              </div>
            )}

            {isLoadingBusinessAccounts ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading workspaces...</span>
              </div>
            ) : visibleWorkspaces && visibleWorkspaces.length > 0 ? (
              <Card>
                <CardContent className="p-4 space-y-3">
                  {visibleWorkspaces.map((businessAccount) => {
                    const isSelected = selectedBusinessAccounts.includes(businessAccount.id);
                    const hasAllAccess = accountPermissions[businessAccount.id]?.has_all_conversations_access || false;

                    return (
                      <div
                        key={businessAccount.id}
                        className={`flex flex-col p-3 rounded-lg border transition-colors ${isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                          }`}
                      >
                        <div
                          className={`flex items-center justify-between w-full ${isSingleAccount || hasOrgWideAccess ? 'cursor-default' : 'cursor-pointer'}`}
                          onClick={
                            hasOrgWideAccess
                              ? undefined
                              : () => handleBusinessAccountToggle(businessAccount.id)
                          }
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="font-medium">{businessAccount.name}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {businessAccount.whatsapp?.phone_number || 'No WhatsApp connected'}
                                </div>
                              </div>
                            </div>
                            {businessAccount.is_default && (
                              <Badge variant="secondary" className="text-xs">
                                Default
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                        </div>

                        {isSelected && formData.role === 'AGENT' && (
                          <div className="mt-3 pl-9 border-t pt-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`perm-all-${businessAccount.id}`}
                                checked={hasAllAccess}
                                onCheckedChange={(checked) => handleValidationToggle(businessAccount.id, !!checked)}
                              />
                              <Label
                                htmlFor={`perm-all-${businessAccount.id}`}
                                className="text-sm cursor-pointer"
                              >
                                View All Conversations
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              Can see and manage all conversations for this business account.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No business accounts found. Create a business account first.
                  </p>
                </CardContent>
              </Card>
            )}

            {selectedBusinessAccounts.length > 0 && (
              <div className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {selectedBusinessAccounts.length} business account{selectedBusinessAccounts.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </form>
      </FloatingDrawerBody>

      <FloatingDrawerFooter>
        <Button
          type="button"
          variant="outline"
          className="rounded-[8px]"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="invite-member-form"
          disabled={
            !formData.email ||
            !formData.name ||
            !formData.password ||
            !!passwordError ||
            (formData.role === 'RIDER' && riderPhone.replace(/\D/g, '').length !== 10)
          }
        >
          Add Member
        </Button>
      </FloatingDrawerFooter>
    </FloatingDrawer>
  );
};
